import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useToast } from '../../components/Toast';
import {
  MOCK_WORKS,
  MOCK_VOTE_TIMELINE,
  MOCK_CONTEST_STATS,
  GRADE_LABEL,
} from '../pilot/rise/contestMockData';
import {
  ArrowLeftIcon,
  DownloadIcon,
  UploadIcon,
  WarningIcon,
  CheckCircleIcon,
} from '../pilot/rise/contestIcons';
import './PilotContestManager.css';

const TABS = [
  { key: 'works', label: '작품 관리' },
  { key: 'votes', label: '투표 현황' },
  { key: 'fraud', label: '부정투표 모니터링' },
  { key: 'upload', label: '일괄 등록' },
];

/**
 * 부정투표 의심 목업 데이터 — 최근 1시간 급증 작품
 */
const MOCK_FRAUD_WATCHLIST = [
  { workId: 'w017', title: '학부모 공개수업 후기 — AI PPT 슬라이드 만드는 교실', recentVotes: 38, baseline: 4, severity: 'high' },
  { workId: 'w015', title: '학급 운영 팁을 생성 모델 3종 비교로 뽑아 쓰기', recentVotes: 21, baseline: 6, severity: 'medium' },
];

const PilotContestManager = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [activeTab, setActiveTab] = useState('works');
  const [works, setWorks] = useState(MOCK_WORKS);
  const [search, setSearch] = useState('');
  const [gradeFilter, setGradeFilter] = useState('all');

  // 작품 목록 필터
  const filteredWorks = useMemo(() => {
    let list = works;
    if (gradeFilter !== 'all') list = list.filter((w) => w.gradeGroup === gradeFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (w) =>
          w.title.toLowerCase().includes(q) ||
          w.authorLabel.toLowerCase().includes(q)
      );
    }
    return [...list].sort((a, b) => b.likeCount - a.likeCount);
  }, [works, search, gradeFilter]);

  const toggleVisible = (workId) => {
    setWorks((prev) => prev.map((w) => (w.id === workId ? { ...w, visible: !(w.visible === false) } : w)));
    const w = works.find((x) => x.id === workId);
    showToast(
      w?.visible === false ? '작품이 다시 공개되었습니다.' : '작품이 비공개 처리되었습니다.',
      'info'
    );
  };

  // 최종 순위 CSV 다운로드 (CouponManager 패턴)
  const downloadRankingCsv = () => {
    const header = ['순위', '작품ID', '제목', '작성자', '학교급', '좋아요 수', '접수일(KST)'];
    const rows = [...works]
      .sort((a, b) => b.likeCount - a.likeCount)
      .map((w, i) => [
        i + 1,
        w.id,
        `"${w.title.replace(/"/g, '""')}"`,
        `"${w.authorLabel.replace(/"/g, '""')}"`,
        GRADE_LABEL[w.gradeGroup] || w.gradeGroup,
        w.likeCount,
        w.createdAtKst.slice(0, 16).replace('T', ' '),
      ]);

    const csv = [header, ...rows].map((r) => r.join(',')).join('\n');
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `contest_ranking_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    showToast('최종 순위 CSV가 다운로드되었습니다.', 'success');
  };

  const handleUploadMock = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    showToast(`"${file.name}" 파일을 미리보기로 확인했어요. 실제 등록은 백엔드 연동 후 활성화됩니다.`, 'info', 5000);
    e.target.value = '';
  };

  return (
    <>
      <Helmet>
        <title>공모전 관리 | 관리자 홈</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="contest-admin">

        {/* ── 헤더 ── */}
        <header className="contest-admin__header">
          <button type="button" className="contest-admin__back" onClick={() => navigate('/admin')}>
            <ArrowLeftIcon width={18} height={18} />
            <span>관리자 홈</span>
          </button>
          <div className="contest-admin__header-main">
            <h1 className="contest-admin__title">공모전 관리</h1>
            <span className="contest-admin__preview-badge">미리보기 · 목업 데이터</span>
          </div>
          <p className="contest-admin__subtitle">
            왓퀴즈로 바뀐 나의 교실 — 2026 AI 활용사례 공모전
          </p>
        </header>

        {/* ── 탭 ── */}
        <nav className="contest-admin__tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              className={`contest-admin__tab ${activeTab === t.key ? 'is-active' : ''}`}
              onClick={() => setActiveTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </nav>

        {/* ═════ 탭 1. 작품 관리 ═════ */}
        {activeTab === 'works' && (
          <section className="contest-admin__panel">
            <div className="contest-admin__panel-head">
              <div>
                <h2 className="contest-admin__panel-title">작품 목록</h2>
                <p className="contest-admin__panel-desc">총 {filteredWorks.length}개 · 좋아요순</p>
              </div>
              <div className="contest-admin__panel-actions">
                <input
                  type="text"
                  className="contest-admin__search"
                  placeholder="제목·작성자 검색"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <select
                  className="contest-admin__select"
                  value={gradeFilter}
                  onChange={(e) => setGradeFilter(e.target.value)}
                >
                  <option value="all">전체 학교급</option>
                  <option value="elementary">초등</option>
                  <option value="middle">중등</option>
                  <option value="high">고등</option>
                  <option value="special">특수</option>
                </select>
                <button
                  type="button"
                  className="contest-admin__btn contest-admin__btn--primary"
                  onClick={downloadRankingCsv}
                >
                  <DownloadIcon width={14} height={14} />
                  <span>최종 순위 CSV</span>
                </button>
              </div>
            </div>

            <div className="contest-admin__table-wrap">
              <table className="contest-admin__table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>제목</th>
                    <th>작성자</th>
                    <th>학교급</th>
                    <th>좋아요</th>
                    <th>접수일(KST)</th>
                    <th>공개</th>
                    <th>작업</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredWorks.map((w, idx) => (
                    <tr key={w.id} className={w.visible === false ? 'is-hidden-row' : ''}>
                      <td className="contest-admin__td-num">
                        {idx < 3
                          ? <span className={`contest-admin__rank-badge contest-admin__rank-badge--${idx + 1}`}>{idx + 1}</span>
                          : idx + 1}
                      </td>
                      <td className="contest-admin__td-title">
                        <span>{w.title}</span>
                      </td>
                      <td className="contest-admin__td-muted">{w.authorLabel}</td>
                      <td>
                        <span className="contest-admin__grade-chip">{GRADE_LABEL[w.gradeGroup]}</span>
                      </td>
                      <td className="contest-admin__td-num">{w.likeCount.toLocaleString()}</td>
                      <td className="contest-admin__td-muted">{w.createdAtKst.slice(0, 10)}</td>
                      <td>
                        <label className="contest-admin__toggle">
                          <input
                            type="checkbox"
                            checked={w.visible !== false}
                            onChange={() => toggleVisible(w.id)}
                          />
                          <span className="contest-admin__toggle-slider" />
                        </label>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="contest-admin__btn contest-admin__btn--ghost"
                          onClick={() => showToast('작품 수정 모달은 백엔드 연동 후 활성화됩니다.', 'info')}
                        >
                          수정
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* ═════ 탭 2. 투표 현황 ═════ */}
        {activeTab === 'votes' && (
          <section className="contest-admin__panel">
            <div className="contest-admin__panel-head">
              <h2 className="contest-admin__panel-title">투표 집계 현황</h2>
              <p className="contest-admin__panel-desc">KST 기준 · 실시간 업데이트 (목업)</p>
            </div>

            <div className="contest-admin__kpi-grid">
              <div className="contest-admin__kpi-card">
                <span className="contest-admin__kpi-label">총 접수 사례</span>
                <span className="contest-admin__kpi-value">{MOCK_CONTEST_STATS.submissionCount}</span>
                <span className="contest-admin__kpi-unit">건</span>
              </div>
              <div className="contest-admin__kpi-card">
                <span className="contest-admin__kpi-label">누적 투표수</span>
                <span className="contest-admin__kpi-value">{MOCK_CONTEST_STATS.totalVotes.toLocaleString()}</span>
                <span className="contest-admin__kpi-unit">표</span>
              </div>
              <div className="contest-admin__kpi-card">
                <span className="contest-admin__kpi-label">고유 투표자</span>
                <span className="contest-admin__kpi-value">{MOCK_CONTEST_STATS.uniqueVoters}</span>
                <span className="contest-admin__kpi-unit">명</span>
              </div>
              <div className="contest-admin__kpi-card">
                <span className="contest-admin__kpi-label">오늘 투표수</span>
                <span className="contest-admin__kpi-value">{MOCK_CONTEST_STATS.todayVotes}</span>
                <span className="contest-admin__kpi-unit">표</span>
              </div>
            </div>

            <div className="contest-admin__chart-card">
              <h3 className="contest-admin__chart-title">일별 투표 추이 (10월 전체)</h3>
              <div className="contest-admin__chart">
                {(() => {
                  const max = Math.max(...MOCK_VOTE_TIMELINE.map((d) => d.votes));
                  return MOCK_VOTE_TIMELINE.map((d) => (
                    <div key={d.date} className="contest-admin__chart-col">
                      <div
                        className="contest-admin__chart-bar"
                        style={{ height: `${(d.votes / max) * 100}%` }}
                        title={`${d.date}: ${d.votes}표`}
                      />
                      <span className="contest-admin__chart-label">{d.date.slice(-2)}</span>
                    </div>
                  ));
                })()}
              </div>
              <p className="contest-admin__chart-foot">
                피크: 10/17 {Math.max(...MOCK_VOTE_TIMELINE.map((d) => d.votes))}표
              </p>
            </div>
          </section>
        )}

        {/* ═════ 탭 3. 부정투표 모니터링 ═════ */}
        {activeTab === 'fraud' && (
          <section className="contest-admin__panel">
            <div className="contest-admin__panel-head">
              <h2 className="contest-admin__panel-title">부정투표 모니터링</h2>
              <p className="contest-admin__panel-desc">
                최근 1시간 내 좋아요가 비정상적으로 급증한 작품 (baseline 대비 5배 이상)
              </p>
            </div>

            {MOCK_FRAUD_WATCHLIST.length === 0 ? (
              <div className="contest-admin__empty">
                <div className="contest-admin__empty-icon">
                  <CheckCircleIcon width={32} height={32} />
                </div>
                <p>현재 감지된 이상 징후가 없어요.</p>
              </div>
            ) : (
              <div className="contest-admin__fraud-list">
                {MOCK_FRAUD_WATCHLIST.map((item) => (
                  <div key={item.workId} className={`contest-admin__fraud-card is-${item.severity}`}>
                    <div className="contest-admin__fraud-head">
                      <span className={`contest-admin__fraud-badge is-${item.severity}`}>
                        <WarningIcon width={12} height={12} />
                        <span>{item.severity === 'high' ? '위험' : '주의'}</span>
                      </span>
                      <span className="contest-admin__fraud-id">{item.workId}</span>
                    </div>
                    <h3 className="contest-admin__fraud-title">{item.title}</h3>
                    <div className="contest-admin__fraud-metrics">
                      <div>
                        <span className="contest-admin__fraud-metric-label">최근 1시간</span>
                        <span className="contest-admin__fraud-metric-value">{item.recentVotes}표</span>
                      </div>
                      <div>
                        <span className="contest-admin__fraud-metric-label">평상시 시간당</span>
                        <span className="contest-admin__fraud-metric-value">{item.baseline}표</span>
                      </div>
                      <div>
                        <span className="contest-admin__fraud-metric-label">배수</span>
                        <span className="contest-admin__fraud-metric-value is-warn">
                          {(item.recentVotes / item.baseline).toFixed(1)}x
                        </span>
                      </div>
                    </div>
                    <div className="contest-admin__fraud-actions">
                      <button
                        type="button"
                        className="contest-admin__btn contest-admin__btn--ghost"
                        onClick={() => showToast('IP/계정별 투표 로그는 백엔드 연동 후 확인 가능합니다.', 'info')}
                      >
                        상세 로그 보기
                      </button>
                      <button
                        type="button"
                        className="contest-admin__btn contest-admin__btn--warn"
                        onClick={() => showToast(`"${item.title}" 최근 1시간 투표를 무효 처리했습니다. (목업)`, 'warning')}
                      >
                        해당 시간 투표 무효화
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* ═════ 탭 4. 일괄 등록 ═════ */}
        {activeTab === 'upload' && (
          <section className="contest-admin__panel">
            <div className="contest-admin__panel-head">
              <h2 className="contest-admin__panel-title">작품 일괄 등록 (CSV)</h2>
              <p className="contest-admin__panel-desc">
                외부 응답지(스프레드시트)나 백업 CSV로부터 작품을 한 번에 가져올 때 사용해요.
              </p>
            </div>

            <div className="contest-admin__upload-card">
              <div className="contest-admin__upload-icon">
                <UploadIcon width={32} height={32} />
              </div>
              <h3 className="contest-admin__upload-title">CSV 파일 업로드</h3>
              <p className="contest-admin__upload-desc">
                컬럼: 제목 · 작성자 라벨 · 학교급 · 요약 · 태그 · 썸네일(선택)
              </p>
              <label className="contest-admin__upload-btn">
                <span>CSV 파일 선택</span>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleUploadMock}
                  style={{ display: 'none' }}
                />
              </label>
              <p className="contest-admin__upload-foot">
                현재는 미리보기 상태예요. 실제 등록은 백엔드 연동 후 열려요.
              </p>
            </div>

            <div className="contest-admin__hint-card">
              <h3 className="contest-admin__hint-title">처리 방식</h3>
              <ol className="contest-admin__hint-list">
                <li>CSV 행을 미리보기 테이블로 표시합니다.</li>
                <li>학교급 자동 매핑 (초/중/고/특수)과 썸네일 그라디언트 자동 할당.</li>
                <li>검토 후 "일괄 등록" 버튼 클릭 시 작품 컬렉션에 추가됩니다.</li>
                <li>등록 즉시 갤러리/대시보드 집계에 반영됩니다.</li>
              </ol>
            </div>
          </section>
        )}

      </div>
    </>
  );
};

export default PilotContestManager;
